import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import ProjectsIcon from '../../assets/Projects.svg';
import TasksIcon from '../../assets/Task.svg';
import DocumentIcon from '../../assets/document-text.svg';

const StatsOverview = ({ dashboardData }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();

  // Extract data from API response or use defaults
  const taskOverview = dashboardData?.task_overview || {};
  const projectOverview = dashboardData?.project_overview || {};
  const documentOverview = dashboardData?.document_overview || {};

  // Handle tile clicks
  const handleTileClick = (route) => {
    navigate(route);
  };

  const statsData = [
    {
      title: 'Tasks Overview',
      stats: [
        { label: 'Pending', value: taskOverview.pending_tasks || '0' },
        { label: 'Completed', value: taskOverview.completed_tasks || '0' }
      ],
      icon: TasksIcon,
      // Match dark card background from design reference, light mode lighter gray
      bgColor: mode === 'dark' ? '#14161F' : '#F3F4F6',
      route: '/manage-task',
      clickable: true
    },
    {
      title: 'Projects Overview', 
      stats: [
        { label: 'Active', value: projectOverview.active_projects || '0' },
        { label: 'Completed', value: projectOverview.completed_projects || '0' }
      ],
      icon: ProjectsIcon,
      bgColor: mode === 'dark' ? '#14161F' : '#F3F4F6',
      route: '/projects',
      clickable: true
    },
    {
      title: 'Documents Overview',
      stats: [
        { label: 'Uploaded', value: documentOverview.uploaded_documents || '0' },
        { label: 'Expired', value: documentOverview.expired_documents || '0' }
      ],
      icon: DocumentIcon,
      bgColor: mode === 'dark' ? '#14161F' : '#F3F4F6',
      route: '/documents',
      clickable: true
    }
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    }}>
      {statsData.map((card, index) => (
        <div
          key={index}
          style={{
            backgroundColor: card.bgColor,
            borderRadius: '12px',
            padding: '24px',
            // Use light text similar to reference design
            color: mode === 'dark' ? '#E5E7EB' : '#111827',
            minHeight: '120px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            cursor: card.clickable ? 'pointer' : 'default',
            transition: 'opacity 0.2s ease',
            opacity: 1
          }}
          onClick={card.clickable ? () => handleTileClick(card.route) : undefined}
          onMouseEnter={card.clickable ? (e) => {
            e.target.style.opacity = '0.9';
          } : undefined}
          onMouseLeave={card.clickable ? (e) => {
            e.target.style.opacity = '1';
          } : undefined}
          title={card.clickable ? `Click to view ${card.title.toLowerCase()}` : undefined}
        >
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px'
          }}>
            <img 
              src={card.icon} 
              alt={card.title}
              style={{ 
                width: '24px', 
                height: '24px',
                filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'invert(42%) sepia(93%) saturate(1352%) hue-rotate(180deg) brightness(119%) contrast(119%)'
              }} 
            />
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              margin: 0,
              color: mode === 'dark' ? '#F9FAFB' : '#111827',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              {card.title}
            </h3>
          </div>

          {/* Stats */}
          <div style={{
            display: 'flex',
            flexDirection: card.stats.length > 1 ? 'row' : 'column',
            gap: card.stats.length > 1 ? '30px' : '10px',
            flex: 1
          }}>
            {card.stats.map((stat, statIndex) => (
              <div key={statIndex} style={{
                flex: card.stats.length > 1 ? 1 : 'none'
              }}>
                <div style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: mode === 'dark' ? '#26c3c0' : '#111827',
                  lineHeight: '1',
                  marginBottom: '6px',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: '400',
                  // Softer grey label text to match image
                  color: mode === 'dark' ? '#9CA3AF' : '#6B7280',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  textShadow: 'none',
                  transform: 'none'
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsOverview;
